import { useEffect, useState, lazy, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import {
  StoreSettingsProvider,
  useStoreSettings,
} from "./contexts/StoreSettingsContext";
import { CartModalProvider } from "./contexts/CartModalContext";
import { Toaster } from "react-hot-toast";
import MainLayout from "./layouts/MainLayout";
import ErrorBoundary from "./components/ErrorBoundary";
import PageErrorBoundary from "./components/PageErrorBoundary";
import SafeLazyMotion from "./components/SafeLazyMotion";
import SafeAnimatePresence from "./components/SafeAnimatePresence";
import AdminLayout from "./components/AdminLayout";
import AdminFullPageLayout from "./components/AdminFullPageLayout";
import AdminRoute from "./components/AdminRoute";
import ScrollToTop from "./components/ScrollToTop";
import ProtectedRoute from "./components/ProtectedRoute";
import FloatingCartButton from "./components/menu/FloatingCartButton";
import { DemoSignupInterceptor } from "./components/DemoSignupInterceptor";
import { useScrollThumb } from "./hooks/useScrollThumb";

// Lazy load all pages for code splitting
const HomePage = lazy(() => import("./pages/HomePage.tsx"));
const MenuPage = lazy(() => import("./pages/MenuPage.tsx"));
const ReservationsPage = lazy(() => import("./pages/ReservationsPage.tsx"));
const AboutPage = lazy(() => import("./pages/AboutPage.tsx"));
const ContactPage = lazy(() => import("./pages/ContactPage.tsx"));
const Products = lazy(() => import("./pages/Products.tsx"));
const ProductDetail = lazy(() => import("./pages/ProductDetail.tsx"));
const Favorites = lazy(() => import("./pages/Favorites.tsx"));
const AddressBook = lazy(() => import("./pages/AddressBook.tsx"));
const Checkout = lazy(() => import("./pages/Checkout.tsx"));
const OrderHistory = lazy(() => import("./pages/OrderHistory.tsx"));
const Signup = lazy(() => import("./pages/Signup.tsx"));
const Login = lazy(() => import("./pages/Login.tsx"));

// Lazy load admin pages
const Admin = lazy(() => import("./pages/Admin.tsx"));
const AdminMenuCategories = lazy(
  () => import("./pages/admin/AdminMenuCategories.tsx"),
);
const AdminMenuItems = lazy(() => import("./pages/admin/AdminMenuItems.tsx"));
const AdminMenuAvailability = lazy(
  () => import("./pages/admin/AdminMenuAvailability.tsx"),
);
const AdminOrders = lazy(() => import("./pages/admin/AdminOrders.tsx"));
const AdminCustomers = lazy(() => import("./pages/admin/AdminCustomers.tsx"));
const AdminDiscountCodes = lazy(
  () => import("./pages/admin/AdminDiscountCodes.tsx"),
);
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings.tsx"));
const AdminAppearance = lazy(() => import("./pages/admin/AdminAppearance.tsx"));
const AdminHomePageControls = lazy(
  () => import("./pages/admin/AdminHomePageControls.tsx"),
);
const AdminFeatureFlags = lazy(
  () => import("./pages/admin/AdminFeatureFlags.tsx"),
);
const AdminManageAdmins = lazy(
  () => import("./pages/admin/AdminManageAdmins.tsx"),
);
const AdminReservations = lazy(
  () => import("./pages/admin/AdminReservations.tsx"),
);
const AdminGallery = lazy(() => import("./pages/admin/AdminGallery.tsx"));
const AdminSpecialSections = lazy(
  () => import("./pages/admin/AdminSpecialSections.tsx"),
);
const AdminFavoriteComments = lazy(
  () => import("./pages/admin/AdminFavoriteComments.tsx"),
);

// Loading component for all pages - minimal and fast
const PageLoading = (): JSX.Element => {
  const [isLightTheme, setIsLightTheme] = useState(() => {
    if (typeof document === "undefined") return false;
    return document.documentElement.classList.contains("theme-light");
  });

  useEffect(() => {
    if (typeof document === "undefined") return;
    const checkTheme = () => {
      setIsLightTheme(
        document.documentElement.classList.contains("theme-light"),
      );
    };
    checkTheme();
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => observer.disconnect();
  }, []);

  return (
    <div
      className="flex items-center justify-center min-h-screen"
      style={{
        backgroundColor: isLightTheme
          ? "rgba(255, 255, 255, 0.95)"
          : "rgba(5, 5, 9, 0.95)",
      }}
    >
      <div className="text-center space-y-4">
        <div className="inline-flex h-12 w-12 animate-spin rounded-full border-4 border-[var(--accent)]/70 border-t-transparent"></div>
        <p className="text-sm text-[var(--text-secondary)]">Loading...</p>
      </div>
    </div>
  );
};

function AppContent(): JSX.Element {
  const location = useLocation();
  const { settings } = useStoreSettings();
  const brightnessSetting = settings?.scroll_thumb_brightness ?? 0.6;

  // Use deferred scroll thumb hook instead of inline effect
  useScrollThumb({ brightness: brightnessSetting, enabled: true });

  // Defer animation observer initialization to avoid blocking initial render
  useEffect(() => {
    if (typeof window === "undefined") return;

    // Defer initialization by 500ms to prioritize content rendering
    const initTimeout = setTimeout(() => {
      const reduceMotionQuery = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      );

      const activateAll = () => {
        document.querySelectorAll("[data-animate]").forEach((el) => {
          if (el instanceof HTMLElement) {
            el.dataset.animateActive = "true";
          }
        });
      };

      if (reduceMotionQuery.matches) {
        activateAll();
        return;
      }

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach(({ isIntersecting, target }) => {
            if (!(target instanceof HTMLElement)) return;
            const once = target.dataset.animateOnce !== "false";
            if (isIntersecting) {
              target.dataset.animateActive = "true";
              if (once) observer.unobserve(target);
            } else if (!once) {
              target.dataset.animateActive = "false";
            }
          });
        },
        { threshold: 0.2, rootMargin: "0px 0px -10%" },
      );

      const tracked = new WeakSet<Element>();
      let mutationScheduled = false;

      const scan = () => {
        document.querySelectorAll("[data-animate]").forEach((el) => {
          if (!(el instanceof HTMLElement)) return;
          if (reduceMotionQuery.matches) {
            el.dataset.animateActive = "true";
            return;
          }
          if (!el.dataset.animateActive) el.dataset.animateActive = "false";
          if (tracked.has(el)) return;
          observer.observe(el);
          tracked.add(el);
        });
      };

      scan();

      const mutationObserver = new MutationObserver(() => {
        if (mutationScheduled) return;
        mutationScheduled = true;
        requestAnimationFrame(() => {
          mutationScheduled = false;
          scan();
        });
      });
      mutationObserver.observe(document.body, {
        childList: true,
        subtree: true,
      });

      const handlePreferenceChange = (event: MediaQueryListEvent) => {
        if (event.matches) {
          mutationObserver.disconnect();
          observer.disconnect();
          activateAll();
        } else {
          scan();
        }
      };
      reduceMotionQuery.addEventListener("change", handlePreferenceChange);

      // Store cleanup for outer effect
      (
        window as unknown as { __animationCleanup?: () => void }
      ).__animationCleanup = () => {
        observer.disconnect();
        mutationObserver.disconnect();
        reduceMotionQuery.removeEventListener("change", handlePreferenceChange);
      };
    }, 500);

    return () => {
      clearTimeout(initTimeout);
      (
        window as unknown as { __animationCleanup?: () => void }
      ).__animationCleanup?.();
    };
  }, []);

  // Don't block rendering - we have defaults immediately, so app can render while fetching
  // Only show loading if absolutely necessary (settings will always be available due to defaults)

  return (
    <ErrorBoundary>
      <div
        className="min-h-screen bg-[var(--bg-main)]"
        style={{
          overflow: "visible",
          overflowX: "visible",
          overflowY: "visible",
          scrollBehavior: "smooth", // Enable smooth scrolling
        }}
        // Removed onWheel and onTouchMove handlers - they were interfering with smooth scrolling
        // Modern browsers handle scroll containment better natively
      >
        <ScrollToTop />
        <FloatingCartButton />
        <DemoSignupInterceptor />
        <Toaster
          position="bottom-center"
          toastOptions={{
            style: {
              background: "#1a1a1a",
              color: "#F9FAFB",
              border: "1px solid rgba(197, 157, 95, 0.3)",
            },
            success: {
              iconTheme: {
                primary: "#C59D5F",
                secondary: "#111",
              },
            },
          }}
        />
        <SafeAnimatePresence mode="wait">
          <Routes location={location} key={location.pathname}>
            {/* Main Routes with New Minimalist Design */}
            <Route
              path="/"
              element={
                <MainLayout>
                  <PageErrorBoundary>
                    <Suspense fallback={<PageLoading />}>
                      <HomePage />
                    </Suspense>
                  </PageErrorBoundary>
                </MainLayout>
              }
            />
            <Route
              path="/menu"
              element={
                <MainLayout>
                  <PageErrorBoundary>
                    <Suspense fallback={<PageLoading />}>
                      <MenuPage />
                    </Suspense>
                  </PageErrorBoundary>
                </MainLayout>
              }
            />
            {/* Redirect old order routes to menu */}
            <Route path="/order" element={<Navigate to="/menu" replace />} />
            <Route
              path="/order-online"
              element={<Navigate to="/menu" replace />}
            />
            <Route
              path="/reservations"
              element={
                <MainLayout>
                  <PageErrorBoundary>
                    <Suspense fallback={<PageLoading />}>
                      <ReservationsPage />
                    </Suspense>
                  </PageErrorBoundary>
                </MainLayout>
              }
            />
            <Route
              path="/about"
              element={
                <MainLayout>
                  <PageErrorBoundary>
                    <Suspense fallback={<PageLoading />}>
                      <AboutPage />
                    </Suspense>
                  </PageErrorBoundary>
                </MainLayout>
              }
            />
            <Route
              path="/contact"
              element={
                <MainLayout>
                  <PageErrorBoundary>
                    <Suspense fallback={<PageLoading />}>
                      <ContactPage />
                    </Suspense>
                  </PageErrorBoundary>
                </MainLayout>
              }
            />

            {/* Legacy/Hidden Routes - Still Accessible but Not in Main Nav */}
            <Route
              path="/products"
              element={
                <MainLayout>
                  <PageErrorBoundary>
                    <Suspense fallback={<PageLoading />}>
                      <Products />
                    </Suspense>
                  </PageErrorBoundary>
                </MainLayout>
              }
            />
            <Route
              path="/products/:id"
              element={
                <MainLayout>
                  <PageErrorBoundary>
                    <Suspense fallback={<PageLoading />}>
                      <ProductDetail />
                    </Suspense>
                  </PageErrorBoundary>
                </MainLayout>
              }
            />
            <Route path="/cart" element={<Navigate to="/menu" replace />} />
            <Route
              path="/favorites"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <PageErrorBoundary>
                      <Suspense fallback={<PageLoading />}>
                        <Favorites />
                      </Suspense>
                    </PageErrorBoundary>
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/addresses"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <PageErrorBoundary>
                      <Suspense fallback={<PageLoading />}>
                        <AddressBook />
                      </Suspense>
                    </PageErrorBoundary>
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/checkout"
              element={
                <MainLayout>
                  <PageErrorBoundary>
                    <Suspense fallback={<PageLoading />}>
                      <Checkout />
                    </Suspense>
                  </PageErrorBoundary>
                </MainLayout>
              }
            />
            <Route
              path="/orders"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <PageErrorBoundary>
                      <Suspense fallback={<PageLoading />}>
                        <OrderHistory />
                      </Suspense>
                    </PageErrorBoundary>
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/order-history"
              element={
                <ProtectedRoute>
                  <MainLayout>
                    <PageErrorBoundary>
                      <Suspense fallback={<PageLoading />}>
                        <OrderHistory />
                      </Suspense>
                    </PageErrorBoundary>
                  </MainLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/signup"
              element={
                <MainLayout>
                  <PageErrorBoundary>
                    <Suspense fallback={<PageLoading />}>
                      <Signup />
                    </Suspense>
                  </PageErrorBoundary>
                </MainLayout>
              }
            />
            <Route
              path="/login"
              element={
                <MainLayout>
                  <PageErrorBoundary>
                    <Suspense fallback={<PageLoading />}>
                      <Login />
                    </Suspense>
                  </PageErrorBoundary>
                </MainLayout>
              }
            />

            {/* Admin Routes - Protected with AdminRoute */}
            <Route
              path="/admin"
              element={
                <ErrorBoundary>
                  <AdminRoute>
                    <Suspense fallback={<PageLoading />}>
                      <AdminLayout />
                    </Suspense>
                  </AdminRoute>
                </ErrorBoundary>
              }
            >
              <Route
                index
                element={
                  <Suspense fallback={<PageLoading />}>
                    <Admin />
                  </Suspense>
                }
              />
              <Route
                path="menu-categories"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <AdminMenuCategories />
                  </Suspense>
                }
              />
              <Route
                path="menu-items"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <AdminMenuItems />
                  </Suspense>
                }
              />
              <Route
                path="menu-availability"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <AdminMenuAvailability />
                  </Suspense>
                }
              />
              <Route
                path="orders"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <AdminOrders />
                  </Suspense>
                }
              />
              <Route
                path="reservations"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <AdminReservations />
                  </Suspense>
                }
              />
              <Route
                path="customers"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <AdminCustomers />
                  </Suspense>
                }
              />
              <Route
                path="special-sections"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <AdminSpecialSections />
                  </Suspense>
                }
              />
              <Route
                path="discount-codes"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <AdminDiscountCodes />
                  </Suspense>
                }
              />
              <Route
                path="gallery"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <AdminGallery />
                  </Suspense>
                }
              />
              <Route
                path="favorite-comments"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <AdminFavoriteComments />
                  </Suspense>
                }
              />
              <Route
                path="settings"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <AdminSettings />
                  </Suspense>
                }
              />
              <Route
                path="appearance"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <AdminAppearance />
                  </Suspense>
                }
              />
              <Route
                path="home-page-controls"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <AdminHomePageControls />
                  </Suspense>
                }
              />
              <Route
                path="feature-flags"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <AdminFeatureFlags />
                  </Suspense>
                }
              />
              <Route
                path="manage-admins"
                element={
                  <Suspense fallback={<PageLoading />}>
                    <AdminManageAdmins />
                  </Suspense>
                }
              />
            </Route>

            {/* Full-Page Admin Routes - No sidebar/navbar, minimal header with back button */}
            <Route
              path="/admin/orders/full"
              element={
                <ErrorBoundary>
                  <AdminRoute>
                    <Suspense fallback={<PageLoading />}>
                      <AdminFullPageLayout
                        title="Order Management"
                        backPath="/admin/orders"
                      >
                        <AdminOrders fullPage={true} />
                      </AdminFullPageLayout>
                    </Suspense>
                  </AdminRoute>
                </ErrorBoundary>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </SafeAnimatePresence>
      </div>
    </ErrorBoundary>
  );
}

function App(): JSX.Element {
  return (
    <ErrorBoundary>
      <SafeLazyMotion strict={false}>
        <Router>
          <AuthProvider>
            <StoreSettingsProvider>
              <CartModalProvider>
                <AppContent />
              </CartModalProvider>
            </StoreSettingsProvider>
          </AuthProvider>
        </Router>
      </SafeLazyMotion>
    </ErrorBoundary>
  );
}

export default App;
